import { useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { currentEmail, getIdToken, signOut } from './auth.js';
import { AUTH_EXPIRED_EVENT } from './api.js';
import { Button, DropLogo, ToastProvider } from './components/ui.js';
import { Login } from './pages/Login.js';
import { Dashboard } from './pages/Dashboard.js';
import { Zones } from './pages/Zones.js';
import { Sensors } from './pages/Sensors.js';
import { Connect } from './pages/Connect.js';
import { Settings } from './pages/Settings.js';

type AuthState = 'checking' | 'in' | 'out';

export function App() {
  const [auth, setAuth] = useState<AuthState>('checking');

  useEffect(() => {
    void getIdToken().then((t) => setAuth(t ? 'in' : 'out'));
  }, []);

  // If any API call finds the session expired, drop back to the login screen.
  useEffect(() => {
    const onExpired = () => setAuth((a) => (a === 'in' ? 'out' : a));
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  if (auth === 'checking') return null;
  if (auth === 'out') return <Login onSignedIn={() => setAuth('in')} />;

  return (
    <ToastProvider>
      <Shell onSignOut={() => setAuth('out')} />
    </ToastProvider>
  );
}

function Shell({ onSignOut }: { onSignOut: () => void }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>();

  // The address lives in an ID-token claim, so it arrives a tick after mount.
  useEffect(() => {
    void currentEmail().then(setEmail);
  }, []);

  const handleSignOut = () => {
    signOut();
    onSignOut();
    navigate('/');
  };

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="topbar">
        <div className="topbar-inner">
          <NavLink to="/" className="brand" aria-label="Irrigation home">
            <DropLogo />
            <span>Irrigation</span>
          </NavLink>
          <nav aria-label="Main">
            <NavLink to="/" end>
              Dashboard
            </NavLink>
            <NavLink to="/zones">Zones</NavLink>
            <NavLink to="/sensors">Sensors</NavLink>
            <NavLink to="/connect">Rachio</NavLink>
            <NavLink to="/settings">Settings</NavLink>
          </nav>
          <div className="topbar-right">
            {email && (
              <span className="who" title={email}>
                {email}
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      {/* `tabIndex={-1}` makes this programmatically focusable: when a dialog
          closes after deleting the row that opened it, Modal hands focus here
          rather than dropping it on <body>. */}
      <main className="container" id="main" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/zones" element={<Zones />} />
          <Route path="/sensors" element={<Sensors />} />
          <Route path="/connect" element={<Connect />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="footer">ET-based scheduling · weather by Open-Meteo · Rachio as the actuator</footer>
    </div>
  );
}
